import { type FC, useCallback, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import type { ShippingFormFields } from "@jti/commerce-types";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Button } from "@jti/core-components/components/Button";
import { newrelic } from "@jti/fe-clients";
import { useFormContext, useTranslations } from "@jti/ui";

import type { AtHomeDeliveryAddressesProps } from "@checkout-v2/features/DeliveryInformation/DeliveryAddresses/DeliveryAddresses.types";
import { OrderAttributesSlot } from "@checkout-v2/features/OrderAttributes";
import {
  AddressHomeDeliveryView,
  useAddressHomeDelivery,
} from "@checkout-v2/formFields/components/AddressHomeDeliveryField";
import { BillingAddressField } from "@checkout-v2/formFields/components/BillingAddressField";
import { UseDifferentBillingAddressField } from "@checkout-v2/formFields/components/UseDifferentBillingAddressField";
import { hasRequiredBillingAddressFields } from "@checkout-v2/forms/ShippingForm/renderer/utils";

import styles from "../../DeliveryAddresses.module.scss";

export const AtHomeDeliveryAddresses: FC<AtHomeDeliveryAddressesProps> = ({
  formFieldsConfig,
  firstname,
  lastname,
  telephone,
  billingAddressCardRef,
  showBillingValidationError = false,
}) => {
  const config = useConfig();
  const { __ } = useTranslations();
  const form = useFormContext<ShippingFormFields>();

  const isBillingAddressSameAsShippingFieldValue = form.watch(formFieldsConfig.isBillingAddressSameAsShipping.name);

  const [billingMode, setBillingMode] = useState<"custom" | "view">(() => {
    const initialBillingAddress = form.getValues(formFieldsConfig.billingAddress.name);
    const isSameAsShipping = form.getValues(formFieldsConfig.isBillingAddressSameAsShipping.name);
    const hasCompleteBillingAddress = hasRequiredBillingAddressFields(initialBillingAddress, {
      isStreetFieldCombined: config.isStreetFieldCombined,
    });

    return !isSameAsShipping && hasCompleteBillingAddress ? "view" : "custom";
  });

  const homeDeliveryState = useAddressHomeDelivery({
    fieldConfig: formFieldsConfig.addressHomeDelivery,
    firstname,
    lastname,
    telephone,
  });

  const shouldRenderBillingSection = config.isBillingAddressVisibleForCheckout;
  const shouldRenderCustomBillingAddress = shouldRenderBillingSection && !isBillingAddressSameAsShippingFieldValue;

  const onBillingAddressToggle = useCallback(
    (usesDifferentBillingAddress: boolean) => {
      if (usesDifferentBillingAddress) {
        form.setValue(formFieldsConfig.billingAddress.name, formFieldsConfig.addressHomeDelivery.emptyAddress);
      } else {
        form.setValue(formFieldsConfig.billingAddress.name, null);
      }

      form.clearErrors(formFieldsConfig.billingAddress.name);
      setBillingMode(usesDifferentBillingAddress ? "custom" : "view");
    },
    [form, formFieldsConfig.addressHomeDelivery.emptyAddress, formFieldsConfig.billingAddress.name]
  );

  const onSaveAddressAndContinue = useCallback(async () => {
    const [isShippingAddressValid, isBillingAddressValid] = await Promise.all([
      homeDeliveryState.validateNewAddress(),
      shouldRenderCustomBillingAddress
        ? form.trigger(formFieldsConfig.billingAddress.name, { shouldFocus: true })
        : Promise.resolve(true),
    ]);

    if (!isShippingAddressValid || !isBillingAddressValid) {
      return;
    }

    try {
      const isShippingAddressSaved = await homeDeliveryState.saveNewAddress();

      if (isShippingAddressSaved && shouldRenderCustomBillingAddress) {
        setBillingMode("view");
      }
    } catch (error) {
      newrelic.noticeError(
        error instanceof Error ? error : new Error("DeliveryAddresses: Failed to save shipping address")
      );
    }
  }, [form, formFieldsConfig.billingAddress.name, homeDeliveryState, shouldRenderCustomBillingAddress]);

  return (
    <div data-testid="delivery-addresses-at-home" className={styles.container}>
      <div data-testid="delivery-addresses-shipping-section" className={styles.shippingSection}>
        {homeDeliveryState.isFormMode && <h4>{__("cart.checkout.shipping_address.title")}</h4>}

        <OrderAttributesSlot slotTarget="shippingAddress_before" />

        <AddressHomeDeliveryView
          fieldConfig={formFieldsConfig.addressHomeDelivery}
          mode={homeDeliveryState.mode}
          immutableProvinceCode={homeDeliveryState.immutableProvinceCode}
          homeDeliveryAddress={homeDeliveryState.homeDeliveryAddress}
          homeDeliveryAddressWithOverrides={homeDeliveryState.homeDeliveryAddressWithOverrides}
          shippingAddressErrorMessage={homeDeliveryState.shippingAddressErrorMessage}
          isAddressDialogOpen={homeDeliveryState.isAddressDialogOpen}
          isSaveAddressPending={homeDeliveryState.isSaveAddressPending}
          isOnDialogSubmitLoading={homeDeliveryState.isOnDialogSubmitLoading}
          showShippingAddressSelector={homeDeliveryState.showShippingAddressSelector}
          hideShippingAddressSelector={homeDeliveryState.hideShippingAddressSelector}
          showNewAddressForm={homeDeliveryState.showNewAddressForm}
          onAddressSelect={homeDeliveryState.onAddressSelect}
          saveNewAddress={homeDeliveryState.saveNewAddress}
          renderSaveButton={false}
        />

        <OrderAttributesSlot slotTarget="shippingAddress_after" />
      </div>

      {shouldRenderBillingSection && (
        <div
          ref={billingAddressCardRef}
          data-testid="delivery-addresses-billing-section"
          className={styles.billingSection}
        >
          {showBillingValidationError && (
            <AlertBanner
              tone="warning"
              description={__("cart.checkout.warning.billing_address_validation_description")}
            />
          )}

          <OrderAttributesSlot slotTarget="billingaddress_before" />

          <UseDifferentBillingAddressField
            fieldConfig={formFieldsConfig.isBillingAddressSameAsShipping}
            onBillingAddressToggle={onBillingAddressToggle}
          />

          {shouldRenderCustomBillingAddress && (
            <BillingAddressField
              fieldConfig={formFieldsConfig.billingAddress}
              mode={billingMode}
              onModeChange={setBillingMode}
            />
          )}

          {shouldRenderCustomBillingAddress && <OrderAttributesSlot slotTarget="billingaddress_after" />}
        </div>
      )}

      {homeDeliveryState.isFormMode && (
        <Button
          type="button"
          onClick={onSaveAddressAndContinue}
          pending={homeDeliveryState.isSaveAddressPending}
          data-testid="delivery-addresses-save-button"
        >
          {__("cart.checkout.shipping_address.new.save")}
        </Button>
      )}
    </div>
  );
};
